import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import config from '../../../config';
import links from '../../../config/links';
import {
    clientType,
    PopupTypes,
    USER_STATUS_FREE,
    USER_STATUS_ANNUAL,
    USER_STATUS_MONTHLY,
} from '../../utils/constants';
import { PowerFunnelSurfaces, PowerFunnelGatedFeatures } from '../Analytics/constants';
import * as popupActions from '../../actions/popup-actions';
import { trackEvent } from '../Analytics/TrackedClick';
import { titles, userParams } from '../PopUps/LoginPopUpWrapper/LoginPopUp/utils';
import { showLoginPopUp } from '../../actions/login-popup-actions';
import { useStatsig } from './useStatsig';
import { userCanAccessGlobalPrivileges, userHasActiveClubPower } from '../../utils/user';

const globalPrivilegesUrl = `${config.apiHost}/v1/saml/globalprivileges`;

// Module-level tracking to ensure impression events only fire once per surface per page load
const trackedImpressionSurfaces = new Set();

export const getCtaState = (user) => {
    const isPowered = !!user?.isPowered;
    const isAnnualSubscription = user?.subscriptionTierType === 'year';
    const isMonthlySubscription = user?.subscriptionTierType === 'month';
    // An actively club-powered member is not in a power-lapse state, so a past-due
    // personal subscription must not drive the "Resume Power" CTA — they keep their
    // powered CTA (Visit Global Privileges). Non-club members are unaffected.
    const hasPastDueSubscription = !!user?.hasPastDueSubscription && !userHasActiveClubPower(user);
    const hasGlobalPrivilegesAccess = userCanAccessGlobalPrivileges(user);

    const userStatus = (() => {
        if (!isPowered) {
            return USER_STATUS_FREE;
        }
        if (isAnnualSubscription) {
            return USER_STATUS_ANNUAL;
        }
        return USER_STATUS_MONTHLY;
    })();

    const ctaType = (() => {
        if (hasGlobalPrivilegesAccess) {
            return 'access_global_privileges';
        }
        if (!isPowered) {
            return 'start_power';
        }
        return 'upgrade_annual';
    })();

    const buttonText = (() => {
        if (hasPastDueSubscription) {
            return 'Resume Power';
        }
        if (hasGlobalPrivilegesAccess) {
            return 'Visit Global Privileges';
        }
        if (isPowered && isMonthlySubscription) {
            return 'Upgrade & Save';
        }
        return 'Sign Up';
    })();

    const findAccommodationsTitle = (() => {
        if (!isPowered) {
            return 'Sign up for a Power annual plan and unlock:';
        }
        if (hasGlobalPrivilegesAccess) {
            return 'Your Power annual plan gives you access to:';
        }
        return 'Upgrade to a Power annual plan and unlock:';
    })();

    return {
        isPowered,
        isMonthlySubscription,
        hasPastDueSubscription,
        hasGlobalPrivilegesAccess,
        userStatus,
        ctaType,
        buttonText,
        findAccommodationsTitle,
    };
};

const useGlobalPrivilegesCta = ({
    surface = PowerFunnelSurfaces.HOME,
    variant,
    trackImpressions = false,
    onDismiss,
} = {}) => {
    const user = useSelector(({ auth }) => auth?.user);
    const dispatch = useDispatch();
    const history = useHistory();
    const { checkGate, featureGates } = useStatsig();
    const useMonthlyUpgradePopup = checkGate(featureGates.POWER_UPGRADE_FROM_MONTHLY_TO_ANNUAL);

    const {
        isPowered,
        isMonthlySubscription,
        hasPastDueSubscription,
        hasGlobalPrivilegesAccess,
        userStatus,
        ctaType,
        buttonText,
        findAccommodationsTitle,
    } = getCtaState(user);

    const pastDueSubscriptionMessage = 'Update your payment method to access Global Privileges travel benefits.';

    const eventProperties = {
        surface,
        ctaType,
        userSubscriptionStatus: userStatus,
        platform: clientType,
        ...(variant !== undefined && { variant }),
    };

    // Fire impression event on mount, only once per surface per page load.
    // Re-running on `user?.id` and `variant` ensures we capture the resolved
    // values when Redux/Statsig hydrate after first paint (otherwise the
    // initial fire would carry the wrong `userStatus` / `ctaType`). The Set
    // guard still dedupes across remounts within a page load.
    useEffect(() => {
        if (!trackImpressions) {
            return;
        }
        const impressionKey = `gp_module_impression_${surface}`;
        if (trackedImpressionSurfaces.has(impressionKey)) {
            return;
        }
        trackEvent('gp_module_impression', eventProperties);
        trackedImpressionSurfaces.add(impressionKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trackImpressions, surface, user?.id, variant]);

    const handleCtaClick = (e) => {
        if (e) {
            e.stopPropagation();
        }

        trackEvent('gp_primary_cta_click', { ...eventProperties, ctaType });

        if (!user) {
            dispatch(showLoginPopUp({
                userParamsNeeded: [userParams.CLAIMED_PROFILE],
                titles: titles.POWER_PERKS,
                type: 'power_perks',
                showCloseIcon: true,
                closeOnClickOutside: true,
            }));
            return;
        }

        if (hasPastDueSubscription) {
            history.push('/subscriptions');
            return;
        }

        if (hasGlobalPrivilegesAccess) {
            window.open(globalPrivilegesUrl, '_blank');
            return;
        }

        const popup = isPowered && isMonthlySubscription && useMonthlyUpgradePopup
            ? PopupTypes.POWER_MONTHLY_UPGRADE_POPUP
            : PopupTypes.UPGRADE_TO_POWER_POPUP;

        dispatch(popupActions.showPopupNew({
            popup,
            referringSurface: surface,
            gatedFeature: PowerFunnelGatedFeatures.GLOBAL_PRIVILEGES,
            onClosePopup: onDismiss,
        }));
    };

    const handleLearnMoreClick = (e) => {
        if (e) {
            e.stopPropagation();
        }
        const eventProps = { ...eventProperties, ctaType: 'learn_more' };
        trackEvent('gp_secondary_cta_click', eventProps);
        window.open(links.powerPlayersPage, '_blank');
    };

    return {
        buttonText,
        ctaType,
        eventProperties,
        findAccommodationsTitle,
        handleCtaClick,
        handleLearnMoreClick,
        hasGlobalPrivilegesAccess,
        hasPastDueSubscription,
        isPowered,
        pastDueSubscriptionMessage,
        user,
        userStatus,
    };
};

export default useGlobalPrivilegesCta;
